import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { loadOnboardingState, persistOnboardingState, resetOnboardingState } from './onboardingStorage';
import { CHECKLIST_ITEMS } from './onboardingSteps';
import { ONBOARDING_EVENTS, trackOnboarding } from './onboardingAnalytics';
import OnboardingContext from './onboardingContext';
import { onboardingEnabled } from '../../brand/tenant';

// Состояние обучения на всё приложение: идёт ли тур, что пройдено в чек-листе.
// Провайдер стоит внутри роутера — ему нужен адрес страницы, чтобы отмечать
// пройденные шаги. Сам контекст и хук к нему лежат в onboardingContext.js.

// Какой пункт чек-листа закрывает заход на страницу. Проверяем по порядку,
// берём первое совпадение.
const ROUTE_RULES = [
  { item: 'profile', test: (path) => path === '/profile' },
  { item: 'client', test: (path) => path.startsWith('/client/create') },
  { item: 'deal', test: (path) => path === '/deals/create' },
];
// Пункты «Отправить заявку в банки» и «Посмотреть решения банков» по адресу
// страницы не определить — их закрывает страница кредита в сделке, когда
// видит реальные обращения и ответы банков (DealCredits.jsx).

export const OnboardingProvider = ({ children }) => {
  const user = useSelector((state) => state.user);
  const userId = user?.id;
  const location = useLocation();

  // У брокеров партнёров обучения нет: ни тура, ни чек-листа, ни кнопки
  // «Пройти обучение заново» — шаги написаны под разделы DOSCAR.
  const available = onboardingEnabled();

  const [tourRunning, setTourRunning] = useState(false);
  const [checklistDone, setChecklistDone] = useState([]);
  // Пока состояние не пришло с сервера, ничего не показываем: иначе человек,
  // который тур уже проходил, увидит его ещё раз.
  const [checklistHidden, setChecklistHidden] = useState(true);
  const [loaded, setLoaded] = useState(false);

  // Чтобы не грузить и не запускать тур повторно при каждом обновлении профиля.
  const loadedFor = useRef(null);
  // Последнее сохранённое состояние — чтобы слать на сервер целиком.
  const stateRef = useRef({ tourSeen: false, checklist: [], checklistHidden: false });

  const startTour = useCallback((reason = 'manual') => {
    setTourRunning(true);
    trackOnboarding(ONBOARDING_EVENTS.tourStarted, { reason });
  }, []);

  const persist = useCallback(
    (patch) => {
      stateRef.current = { ...stateRef.current, ...patch };
      persistOnboardingState(userId, stateRef.current);
    },
    [userId],
  );

  // Первый вход: состояние берём с сервера и, если тур ещё не проходили,
  // запускаем сами. Пользователь без id (профиль ещё не загрузился) ждёт —
  // иначе покажем тур на пустой странице.
  useEffect(() => {
    if (!available || !userId || loadedFor.current === userId) return;
    loadedFor.current = userId;

    let cancelled = false;

    loadOnboardingState(userId).then((state) => {
      if (cancelled) return;

      stateRef.current = state;
      setChecklistDone(state.checklist);
      setChecklistHidden(state.checklistHidden);
      setLoaded(true);

      if (!state.tourSeen) {
        setChecklistHidden(false);
        startTour('first_login');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId, startTour]);

  const finishTour = useCallback(
    (outcome, stepId, stepNumber) => {
      setTourRunning(false);
      persist({ tourSeen: true });

      if (outcome === 'skipped') {
        trackOnboarding(ONBOARDING_EVENTS.tourSkipped, { stepId, stepNumber });
      } else {
        trackOnboarding(ONBOARDING_EVENTS.tourFinished, { steps: stepNumber });
      }
    },
    [persist],
  );

  const completeChecklistItem = useCallback(
    (item) => {
      if (!CHECKLIST_ITEMS.includes(item)) return;

      setChecklistDone((prev) => {
        if (prev.includes(item)) return prev;

        const next = CHECKLIST_ITEMS.filter((name) => prev.includes(name) || name === item);
        persist({ checklist: next });
        trackOnboarding(ONBOARDING_EVENTS.checklistItemDone, { item, done: next.length });
        return next;
      });
    },
    [persist],
  );

  // Пункты, которые закрываются просто заходом на нужную страницу. Пока идёт
  // тур — не закрываем: по разделам ходит он, а не человек.
  useEffect(() => {
    if (!loaded || checklistHidden || tourRunning) return;

    const rule = ROUTE_RULES.find((item) => item.test(location.pathname));
    if (rule) completeChecklistItem(rule.item);
  }, [location.pathname, loaded, checklistHidden, tourRunning, completeChecklistItem]);

  const hideChecklist = useCallback(
    (reason = 'manual') => {
      setChecklistHidden(true);
      persist({ checklistHidden: true });
      trackOnboarding(ONBOARDING_EVENTS.checklistHidden, { reason, done: checklistDone.length });
    },
    [persist, checklistDone.length],
  );

  const restartOnboarding = useCallback(() => {
    if (!available) return;

    stateRef.current = { tourSeen: false, checklist: [], checklistHidden: false };
    resetOnboardingState(userId);

    setChecklistDone([]);
    setChecklistHidden(false);
    startTour('restart');
  }, [available, userId, startTour]);

  const value = useMemo(
    () => ({
      available,
      tourRunning: available && tourRunning,
      startTour,
      finishTour,
      checklistDone,
      checklistHidden: !available || checklistHidden,
      completeChecklistItem,
      hideChecklist,
      restartOnboarding,
      allChecklistDone: CHECKLIST_ITEMS.every((item) => checklistDone.includes(item)),
    }),
    [
      available,
      tourRunning,
      startTour,
      finishTour,
      checklistDone,
      checklistHidden,
      completeChecklistItem,
      hideChecklist,
      restartOnboarding,
    ],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export { useOnboarding } from './onboardingContext';

export default OnboardingContext;
